import { useState, useMemo } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Card, CardContent, Input, cn } from '@pantry/ui';
import { useProductGroups, useDivergenceStats } from '@pantry/api-client';
import type { ProductGroupSummary } from '@pantry/api-client';
import {
  ArrowLeftIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  RectangleStackIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { NewGroupDialog } from '../../components/product-groups/NewGroupDialog';
import { GroupDetailPanel } from '../../components/product-groups/GroupDetailPanel';
import { ResetToDefaultsDialog } from '../../components/product-groups/ResetToDefaultsDialog';
import { SyncBadge } from '../../components/product-groups/SyncBadge';

export const Route = createFileRoute('/_app/households_/$householdId_/product-groups')({
  component: ProductGroupsPage,
});

function ProductGroupsPage() {
  const { t } = useLingui();
  const { householdId } = Route.useParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const searchParams = searchQuery ? { search: searchQuery } : undefined;
  const { data: groups, isLoading, error } = useProductGroups(householdId, searchParams);
  const { data: stats } = useDivergenceStats(householdId);

  const selectedGroup = groups?.find((g) => g.id === selectedGroupId) ?? null;

  const groupsByCategory = useMemo(() => {
    if (!groups) return new Map<string, ProductGroupSummary[]>();
    const map = new Map<string, ProductGroupSummary[]>();
    for (const group of groups) {
      const cat = group.category ?? 'Uncategorized';
      const existing = map.get(cat) ?? [];
      existing.push(group);
      map.set(cat, existing);
    }
    return map;
  }, [groups]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/households/$householdId"
          params={{ householdId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Household</Trans>
        </Link>
        <Card className="rounded-xl">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                <ExclamationTriangleIcon className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-destructive font-medium">
                {error.message || t`Failed to load product groups`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasGroups = groups && groups.length > 0;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div>
        <Link
          to="/households/$householdId"
          params={{ householdId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Household</Trans>
        </Link>
      </div>

      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-4 items-center">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10">
            <RectangleStackIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans context="page_title">Product Groups</Trans>
            </h1>
            {stats && (
              <p className="text-sm text-muted-foreground">
                <Trans>
                  {stats.synced} of {stats.total} synced with defaults
                </Trans>
              </p>
            )}
          </div>
        </div>
        <Button onClick={() => setShowNewGroupDialog(true)} className="rounded-xl h-10">
          <PlusIcon className="h-4 w-4 mr-2" />
          <Trans context="action_button">New Group</Trans>
        </Button>
      </div>

      {/* Search bar */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t`Search groups…`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 rounded-xl"
        />
      </div>

      {/* Main content: two-panel layout when a group is selected */}
      <div className={cn('flex gap-6', selectedGroup ? 'flex-col lg:flex-row' : 'flex-col')}>
        {/* Group list */}
        <div className={cn('flex-1 min-w-0', selectedGroup && 'lg:max-w-sm')}>
          {!hasGroups ? (
            <Card className="rounded-2xl animate-in fade-in duration-300">
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                    <RectangleStackIcon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    {searchQuery ? (
                      <Trans>No groups found</Trans>
                    ) : (
                      <Trans>No product groups yet</Trans>
                    )}
                  </h3>
                  {!searchQuery && (
                    <>
                      <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
                        <Trans>
                          Product groups help organize your shopping list items. Create a custom
                          group or wait for automatic groups to be generated.
                        </Trans>
                      </p>
                      <Button
                        className="mt-6 rounded-xl h-11"
                        onClick={() => setShowNewGroupDialog(true)}
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        <Trans context="action_button">Create First Group</Trans>
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {Array.from(groupsByCategory.entries()).map(([category, categoryGroups]) => {
                const isCollapsed = collapsedCategories.has(category);
                return (
                  <Card key={category} className="rounded-xl overflow-hidden">
                    {/* Category header */}
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                    >
                      <span className="font-semibold text-sm text-foreground">
                        {category}
                        <span className="ml-2 text-muted-foreground font-normal">
                          ({categoryGroups.length})
                        </span>
                      </span>
                      {isCollapsed ? (
                        <ChevronRightIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </button>

                    {/* Group rows */}
                    {!isCollapsed && (
                      <div className="divide-y divide-border/50">
                        {categoryGroups.map((group) => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() =>
                              setSelectedGroupId((prev) => (prev === group.id ? null : group.id))
                            }
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors',
                              selectedGroup?.id === group.id && 'bg-primary/5'
                            )}
                          >
                            {group.icon && (
                              <span className="text-base flex-shrink-0">{group.icon}</span>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-foreground text-sm truncate">
                                {group.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                <Plural
                                  value={group.memberCount}
                                  one="# product"
                                  other="# products"
                                />
                              </p>
                            </div>
                            <SyncBadge status={group.syncStatus} className="flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Footer: reset to defaults */}
          {hasGroups && (
            <div className="pt-4 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowResetDialog(true)}
                className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 h-9"
              >
                <Trans context="action_button">Reset All to Defaults</Trans>
              </Button>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedGroup && (
          <div className="flex-1 min-w-0">
            <Card className="rounded-xl">
              <CardContent className="py-5 px-5">
                <GroupDetailPanel
                  householdId={householdId}
                  group={selectedGroup}
                  allGroups={groups ?? []}
                  onClose={() => setSelectedGroupId(null)}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <NewGroupDialog
        householdId={householdId}
        open={showNewGroupDialog}
        onOpenChange={setShowNewGroupDialog}
      />

      <ResetToDefaultsDialog
        householdId={householdId}
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
      />
    </div>
  );
}
